import { useEffect, useRef, useState } from "react";

export interface SelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  label: string;
  options: SelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}

export default function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2 text-sm transition-colors cursor-pointer ${
          selected.length > 0
            ? "border-primary-400 bg-primary-500/10 text-primary-400"
            : "border-background-300 bg-background-100 text-foreground-700 hover:border-primary-400"
        }`}
      >
        {label}
        {selected.length > 0 && (
          <span className="rounded-full bg-primary-500 px-1.5 text-[10px] font-medium text-foreground-950">
            {selected.length}
          </span>
        )}
        <i
          className={`ri-arrow-down-s-line text-base transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 max-h-72 w-60 overflow-auto rounded-lg border border-background-300 bg-background-100 p-2 shadow-lg">
          {options.map((opt) => {
            const active = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-background-200 cursor-pointer"
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    active
                      ? "border-primary-500 bg-primary-500 text-foreground-950"
                      : "border-background-400"
                  }`}
                >
                  {active && <i className="ri-check-line text-xs" />}
                </span>
                <span
                  className={active ? "text-primary-400" : "text-foreground-700"}
                >
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}